import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import contaReceberApi from '../api/apiContaReceber';
import cobrancaPixApi from '../api/apiCobrancaPix';
import { formatarDinheiro } from '../helper';
import { TIPO_PAGAMENTO_DUPLICATA_PIX, TIPO_PAGAMENTO_TOTAL } from '../constants';
import {
  DadosContaReceberBaixar,
  DadosListagemContasReceberCliente
} from '../types/ContaReceberTypes';
import { ResCobrancaPixImediata } from '../types/SicoobTypes';
import usePixExpiracao from './usePixExpiracao';

type UsePixPagamentoParams = {
  dados: DadosListagemContasReceberCliente;
  onEncerrar?: () => void;
  intervaloConsultaMs?: number;
  carregandoInicial?: boolean;
};

type UsePixPagamentoRetorno = {
  qrCode: ResCobrancaPixImediata | null;
  carregando: boolean;
  dataExpiracao: string | null;
  tempoTotalSegundos: number;
  expirado: boolean;
  progresso: number;
  tempoRestanteFormatado: string;
  gerar: () => Promise<void>;
};

const usePixPagamento = ({
  dados,
  onEncerrar,
  intervaloConsultaMs = 5000,
  carregandoInicial = false
}: UsePixPagamentoParams): UsePixPagamentoRetorno => {
  const [qrCode, setQRCode] = useState<ResCobrancaPixImediata | null>(null);
  const [carregando, setCarregando] = useState(carregandoInicial);
  const qrCodeRef = useRef<ResCobrancaPixImediata | null>(null);

  useEffect(() => {
    qrCodeRef.current = qrCode;
  }, [qrCode]);

  const dataExpiracao = qrCode?.calendario?.criacao
    ? dayjs(qrCode.calendario.criacao)
        .add(qrCode.calendario.expiracao, 'seconds')
        .toISOString()
    : null;

  const tempoTotalSegundos = qrCode?.calendario?.expiracao ?? 0;

  const { expirado, progresso, formatado } = usePixExpiracao({
    dataExpiracao,
    tempoTotalSegundos
  });

  const baixarContaReceber = useCallback(
    async (valorPago: string) => {
      const dadosBaixarContaReceber: DadosContaReceberBaixar = {
        codigoEmpresa: dados.codigoEmpresa,
        codigoCliente: dados.codigoCliente,
        financeira: dados.financeira,
        fatura: dados.fatura,
        valorPago: valorPago,
        dataPagamento: String(dayjs().date() + dayjs().month() + dayjs().year()),
        tipoPagamento: TIPO_PAGAMENTO_TOTAL,
        valorAcrescimoAnterior: formatarDinheiro(dados.valores.valorAcrescimo),
        valorDesconto: formatarDinheiro(dados.valores.valorDesconto),
        juros: formatarDinheiro(dados.valores.juros),
        formaPagamento: TIPO_PAGAMENTO_DUPLICATA_PIX,
        valorDescontoAnterior: dados.valores.valorDesconto.toString()
      };

      try {
        await contaReceberApi.baixarContaReceber(dadosBaixarContaReceber);
      } catch (error: any) {
        console.error(error);
      }
    },
    [dados]
  );

  const consultarPagamento = useCallback(async () => {
    try {
      const codigoEmpresa = dados.codigoEmpresa;
      const codigoBanco = dados?.codigoBanco?.toString();

      const res = await contaReceberApi.consultarCobrancaImediata(
        qrCodeRef.current?.txid ?? '',
        codigoEmpresa,
        codigoBanco
      );

      if (res.status === 'CONCLUIDA') {
        if (res.pix && res.pix.length > 0) {
          await cobrancaPixApi.update(
            {
              fatura: dados.fatura
            },
            { dataPagamento: res.pix[0].horario }
          );
          await baixarContaReceber(res.valor.original);

          await contaReceberApi.baixarBoletoSeExistir({
            codigoEmpresa: dados.codigoEmpresa,
            codigoCliente: dados.codigoCliente,
            codigoFinanceira: dados.financeira,
            codigoFatura: dados.fatura
          });
          toast.success('Pagamento Confirmado');
          onEncerrar?.();
        }
      }
    } catch (erro: any) {
      console.error('O sistema não conseguiu verificar o pagamento', erro);
    }
  }, [dados, baixarContaReceber, onEncerrar]);

  useEffect(() => {
    if (!qrCode || expirado) return;

    const id = window.setInterval(() => {
      consultarPagamento();
    }, intervaloConsultaMs);

    return () => {
      window.clearInterval(id);
    };
  }, [qrCode, expirado, consultarPagamento, intervaloConsultaMs]);

  const gerar = useCallback(async () => {
    try {
      setCarregando(true);
      const res = await contaReceberApi.gerarQrCodePix({
        codigoCliente: dados.codigoCliente,
        codigoEmpresa: dados.codigoEmpresa,
        fatura: dados.fatura,
        financeira: dados.financeira
      });
      setQRCode(res);
    } catch (erro: any) {
      if (erro?.response?.status === 409) {
        toast.info(
          erro?.response?.data?.message ||
            'Este PIX já foi pago. O pagamento será registrado em breve.'
        );
      } else {
        toast.error('Falha ao gerar o QR CODE.');
      }
      console.error('Erro:', erro);
      onEncerrar?.();
    } finally {
      setCarregando(false);
    }
  }, [dados, onEncerrar]);

  return {
    qrCode,
    carregando,
    dataExpiracao,
    tempoTotalSegundos,
    expirado,
    progresso,
    tempoRestanteFormatado: formatado,
    gerar
  };
};

export default usePixPagamento;
